import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { BrandType, TripTypology } from '@ess-front/shared';
import { TalkJsUserRole } from '@shared/models/enum/talkjs-user-role.enum';
import { QuoteBE } from '@shared/models/itinerary/quote-be.model';
import { BucketDestinationBE, TripBE } from '@shared/models/itinerary/trip-be.model';
import { Trip } from '@shared/models/itinerary/trip.model';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { QuoteService } from './quote.service';
import { Status } from '@shared/models/itinerary/status.enum';
import { BrandBE } from '@shared/models/itinerary/brand-object-be.model';
import { Brand } from '@shared/models/itinerary/brand-object.model';

@Injectable({
  providedIn: 'root',
})
export class TripService {
  private apiURL = `${environment.apiEnv}api/`;
  private readonly BELMOND_DESKTOP_LOGO =
    'https://res.cloudinary.com/thebellhop/image/upload/v1688727142/assets/logos/belmond_corporate_logo.svg';
  private readonly BELMOND_MOBILE_LOGO =
    'https://res.cloudinary.com/thebellhop/image/upload/v1688727142/assets/logos/belmond_corporate_logo.svg';

  constructor(
    private httpClient: HttpClient,
    private quoteService: QuoteService,
  ) {}

  getTrip$(hash: string): Observable<Trip> {
    const url = `${this.apiURL}v2/bookings/${hash}`;
    return this.httpClient.get<TripBE>(url).pipe(
      switchMap(tripBE => {
        return combineLatest([of(tripBE), this.getTripQuote$(tripBE.hash, tripBE.typology, tripBE.ttv_forecast)]);
      }),
      map(([tripBE, totalPrice]) => this.transformIntoTrip(tripBE, totalPrice)),
    );
  }

  getTripQuote$(hash: string, typology: TripTypology | null, forecast: string | null): Observable<string | null> {
    if (typology === TripTypology.REAL) {
      return this.quoteService.getQuote$(hash).pipe(this.transformQuoteIntoTotalPrice$);
    } else {
      return of(forecast);
    }
  }

  private transformIntoTrip(tripBE: TripBE, totalPrice: string | null): Trip {
    return {
      bookNowStatus: tripBE.book_now_status,
      brand: this.getBrand(tripBE.brand_object, tripBE.partnership),
      curatedBy: {
        avatar: null,
        firstName: tripBE.curated_by?.first_name || 'Essentialist',
        lastName: tripBE.curated_by?.last_name ?? '',
      },
      dayIds:
        tripBE.days?.map(dayBE => {
          return { id: dayBE.id, dayNumber: dayBE.day_number };
        }) ?? [],
      destinations: tripBE.bucket_destinations ?? [],
      destinationLabels:
        tripBE.bucket_destinations && tripBE.bucket_destinations.length > 0
          ? this.getDestinationLabel(tripBE.bucket_destinations)
          : '',
      travelDesigner: tripBE.author
        ? {
            avatar: tripBE.author.avatar ?? null,
            bio: null,
            email: null,
            firstName: tripBE.author.first_name ?? null,
            id: tripBE.author.id,
            lastName: tripBE.author.last_name ?? null,
            phone: null,
            talkJsRole: TalkJsUserRole.STAFF,
          }
        : null,
      endDate: tripBE.end_date,
      hash: tripBE.hash,
      image: tripBE.featured_image ?? '',
      isAmex: tripBE.is_amex,
      isMessagingEnabled: tripBE.is_messaging_system_enabled,
      isSample: tripBE.typology === TripTypology.SAMPLE || tripBE.typology == TripTypology.SUPER_SAMPLE,
      member: tripBE.user
        ? {
            avatar: null,
            firstName: tripBE.user?.first_name ?? null,
            lastName: tripBE.user?.last_name ?? null,
          }
        : null,
      numberOfDays: tripBE.number_of_days,
      publicStatus: tripBE.public_status,
      reference: tripBE.reference,
      season: tripBE.seasons?.join(', ') ?? null,
      startDate: tripBE.start_date,
      status: (tripBE.status as Status) ?? null,
      summary: tripBE.summary,
      title: tripBE.title,
      totalPrice: totalPrice?.match('[1-9]') ? totalPrice : null,
      typology: tripBE.typology,
      visibleSummary: tripBE.visible_summary,
    };
  }
  private transformQuoteIntoTotalPrice$(quote$: Observable<QuoteBE>): Observable<string> {
    return quote$.pipe(map(quote => quote.total_invoiceable_sell_rate_price));
  }

  private getDestinationLabel(destinationBE: BucketDestinationBE[]): string {
    return destinationBE.map(destination => destination.title).join(', ');
  }

  private getBrand(brandBE: BrandBE, partnership?: BrandType): Brand {
    // As long as Belmond is not standardized as brand only, we have to check first if the trip partnership is Belmond to add the brand object as Belmond
    if (partnership === BrandType.BELMOND) {
      return {
        desktopLogo: this.BELMOND_DESKTOP_LOGO,
        mobileLogo: this.BELMOND_MOBILE_LOGO,
        name: 'Belmond',
        slug: BrandType.BELMOND,
      };
    }
    return {
      desktopLogo: brandBE.logo,
      mobileLogo: brandBE.logo_mobile,
      name: brandBE.name,
      slug: brandBE.slug,
    };
  }
}
